
import React, { useState, useEffect } from 'react';
import Header from './components/Header';
import Hero from './components/Hero';
import Footer from './components/Footer';
import ProductCard from './components/ProductCard';
import CartDrawer from './components/Modals/CartDrawer';
import AdminPanel from './components/Admin/AdminPanel';
import { CATEGORIES } from './constants';
import { AppProvider, useApp } from './AppContext';
import { Package, ShieldCheck, CreditCard, Gift, ArrowRight, Star, Settings, Loader2 } from 'lucide-react';

const ProductSkeleton = () => (
  <div className="bg-white dark:bg-slate-800 rounded-2xl overflow-hidden shadow-sm border border-slate-100 dark:border-slate-700 p-4">
    <div className="aspect-square rounded-xl animate-shimmer mb-4"></div>
    <div className="h-4 w-2/3 animate-shimmer mb-2 rounded"></div>
    <div className="h-4 w-full animate-shimmer mb-4 rounded"></div>
    <div className="h-6 w-1/3 animate-shimmer rounded"></div>
  </div>
);

const AppContent: React.FC = () => {
  const { products, siteSettings, isAdminMode, setIsAdminMode, isLoading } = useApp();
  const [timeLeft, setTimeLeft] = useState({ h: 12, m: 45, s: 0 });

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.s > 0) return { ...prev, s: prev.s - 1 };
        if (prev.m > 0) return { ...prev, m: prev.m - 1, s: 59 };
        if (prev.h > 0) return { ...prev, h: prev.h - 1, m: 59, s: 59 };
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  if (isAdminMode) {
    return <AdminPanel />;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow">
        {isLoading ? (
          <div className="h-[700px] bg-slate-200 animate-shimmer"></div>
        ) : (
          <Hero />
        )}

        {/* Benefits Section */}
        <section className="bg-indigo-600 text-white py-12">
          <div className="container mx-auto px-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/10 rounded-2xl">
                <Package className="w-6 h-6" />
              </div>
              <div>
                <h4 className="font-bold">Free Shipping</h4>
                <p className="text-indigo-100 text-sm">On all orders over ${siteSettings.freeShippingThreshold}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/10 rounded-2xl">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <div>
                <h4 className="font-bold">Secure Payment</h4>
                <p className="text-indigo-100 text-sm">100% Secure Transaction</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/10 rounded-2xl">
                <CreditCard className="w-6 h-6" />
              </div>
              <div>
                <h4 className="font-bold">Member Gifts</h4>
                <p className="text-indigo-100 text-sm">Exclusive rewards system</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/10 rounded-2xl">
                <Gift className="w-6 h-6" />
              </div>
              <div>
                <h4 className="font-bold">Daily Deals</h4>
                <p className="text-indigo-100 text-sm">New discounts every 24h</p>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Categories */}
        <section id="categories" className="py-20 container mx-auto px-4">
          <div className="flex justify-between items-end mb-12">
            <div>
              <span className="text-indigo-600 font-bold uppercase tracking-widest text-xs mb-2 block">Collections</span>
              <h2 className="text-4xl font-bold font-serif">Shop by Category</h2>
            </div>
            <a href="#" className="flex items-center gap-2 text-indigo-600 font-bold hover:gap-3 transition-all">
              View All Categories <ArrowRight className="w-4 h-4" />
            </a>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-6">
            {CATEGORIES.map((cat) => (
              <a 
                key={cat.id} 
                href={`#cat-${cat.id}`}
                className="group relative h-72 rounded-3xl overflow-hidden shadow-sm transition-all hover:shadow-xl hover:-translate-y-1"
              >
                <img src={cat.image} alt={cat.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex flex-col justify-end p-6 text-white">
                  <span className="text-3xl mb-2">{cat.icon}</span>
                  <h3 className="text-xl font-bold leading-tight">{cat.name}</h3>
                </div>
              </a>
            ))}
          </div>
        </section>

        {/* Featured Products */}
        <section id="featured" className="py-24 container mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-indigo-600 font-bold uppercase tracking-widest text-xs mb-2 block">Our Picks</span>
            <h2 className="text-4xl font-bold font-serif mb-4">Featured Recommendations</h2>
            <p className="text-slate-500">Hand-selected items from our collection that we know you'll absolutely love.</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {isLoading ? (
              [...Array(4)].map((_, i) => <ProductSkeleton key={i} />)
            ) : (
              products.filter(p => p.isFeatured).map(product => (
                <ProductCard key={product.id} product={product} />
              ))
            )}
          </div>
        </section>

        {/* New Arrivals */}
        <section id="new" className="py-24 bg-slate-100 dark:bg-slate-900/50">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row justify-between items-center mb-16 gap-4">
              <h2 className="text-4xl font-bold font-serif">New Arrivals</h2>
              <div className="flex gap-4">
                <button className="px-6 py-2 bg-white dark:bg-slate-800 rounded-full font-bold text-sm shadow-sm hover:shadow-md transition-all border border-slate-200 dark:border-slate-700">
                  Collections
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {isLoading ? (
                [...Array(4)].map((_, i) => <ProductSkeleton key={i} />)
              ) : (
                products.filter(p => p.isNew).map(product => (
                  <ProductCard key={product.id} product={product} />
                ))
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <CartDrawer />

      {/* Admin Quick Entry - Floating Button */}
      <button 
        onClick={() => setIsAdminMode(true)}
        className="fixed bottom-6 right-6 w-12 h-12 bg-slate-900 text-white rounded-full flex items-center justify-center shadow-2xl hover:bg-indigo-600 transition-all z-40 group"
        title="Admin Panel"
      >
        <Settings className="w-5 h-5 group-hover:rotate-90 transition-transform" />
      </button>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <AppProvider>
      <AppContent />
    </AppProvider>
  );
};

export default App;
